import {
  call,
  put,
  all,
  spawn,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import request from '../../utils/request';
import {
  GET_NOTIFICATION_CHANNEL_DETAILS_URL,
  ADD_NOTIFICATION_CHANNEL_DETAILS_URL,
  UPDATE_NOTIFICATION_CHANNEL_DETAILS_URL,
  GET_MERCHANT_CONFIG_URL,
  UPDATE_MERCHANT_CONFIG_URL,
  FETCH_API_KEYS_URL,
  TEST_WEBHOOK_URL,
  AUDIT_HEADER,
} from '../../utils/constants';
import { showToast } from '../Toast/actions';

import { disableLoaderAction, updateAbandonedCheckoutSecretKeyAction } from './actions';
import {
  GET_WEBHOOK_NOTIFICATIONS,
  GET_WEBHOOK_NOTIFICATIONS_URL,
  UPDATE_WEBHOOK_NOTIFICATIONS_STATUS_URL,
  GET_NOTIFICATION_CHANNEL_DETAILS,
  UPDATE_NOTIFICATION_CHANNEL_DETAILS,
  GET_MERCHANT_CONFIG,
  ADD_NOTIFICATION_CHANNEL_DETAILS,
  UPDATE_MERCHANT_CONFIG,
  TEST_WEBHOOK,
  GET_MERCHANT_KEY,
  GET_NOTIFICATION_CONFIG_VERSION_URL,
  GET_SUBSCRIPTIONS_WEBHOOK_URL,
  GET_SUBSCRIPTIONS_WEBHOOK,
  UPDATE_SUBSCRIPTIONS_WEBHOOK_URL,
  UPDATE_SUBSCRIPTIONS_WEBHOOK,
  GET_CONFIGURED_WEBHOOKS,
  GET_CONFIGURED_WEBHOOKS_URL,
  GET_ALL_NOTIFICATION_TYPES_URL,
  GET_ALL_NOTIFICATION_TYPES,
  CREATE_BULK_NOTIFICATION_CHANNEL_URL,
  CREATE_BULK_NOTIFICATION_CHANNEL,
  TRANSACTION,
  SUBSCRIPTIONS,
  GET_ALL_NOTIFICATION_VERSIONS,
  UPDATE_WEBHOOK_VERSIONS,
  EDIT,
  SAVE_ABANDONED_CHECKOUT_WEBHOOK_URL,
  SAVE_ABANDONED_CHECKOUT_WEBHOOK,
  GET_ABANDONED_CHECKOUT_SECRET_KEY,
  GET_ABANDONED_CHECKOUT_SECRET_KEY_URL,
  UPDATE_ABANDONED_CHECKOUT_SECRET_KEY_URL,
} from './constants';

export const DEVELOPER_WEBHOOK_AUDIT = 'developer-webhook-audit';

export function* getAllNotificationVersions({ data: { cb, ...body } }: any) {
  try {
    const { url, method } = GET_NOTIFICATION_CONFIG_VERSION_URL;

    const options = {
      method,
      queryString: body,
    };

    const response = yield call(request, url, options);

    if (cb) cb(response);
  } catch (e) {
    yield put(showToast(e));
  }
}

export function* getWebhookNotifications({ data: { cb, type } }: any) {
  try {
    const { url, method } = GET_WEBHOOK_NOTIFICATIONS_URL;

    let promiseArr = [];

    type.forEach(event => {
      const options = {
        method,
        queryString: { type: event, channel: 'WEBHOOK' },
      };
      // eslint-disable-next-line redux-saga/yield-effects
      promiseArr.push(call(request, url, options));
    });

    const response = yield all(promiseArr);

    if (cb) cb(response);
  } catch (err) {
    cb();
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getNotificationChannelDetails({ data }: any) {
  try {
    const { cb, key, ...options } = data;
    const { url, method } = GET_NOTIFICATION_CHANNEL_DETAILS_URL;

    const response = yield call(request, url, { method, queryString: options });
    cb({
      key,
      type: data.notificationType,
      response,
    });
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* updateNotificationChannelDetails({
  data: { cb, key, type = 'EDIT', ...body },
}: any) {
  try {
    const { url, method } = UPDATE_NOTIFICATION_CHANNEL_DETAILS_URL;

    const headers = {
      [AUDIT_HEADER]: DEVELOPER_WEBHOOK_AUDIT,
    };

    if (key === TRANSACTION || key === SUBSCRIPTIONS) {
      const { removedEvents } = body;
      const promiseArr = [];
      removedEvents.forEach(event => {
        promiseArr.push(
          // eslint-disable-next-line redux-saga/yield-effects
          call(request, url, { method, body: JSON.stringify(event), headers }),
        );
      });
      yield all(promiseArr);
      if (cb) cb();
    } else {
      const response = yield call(request, url, {
        method,
        body: JSON.stringify(body),
        headers,
      });
      // handling the case when response is null
      cb([{ ...(response || body) }], key);
    }
    if (type === 'EDIT') {
      yield put(
        showToast({
          message: `Response code: 200. Webhook updated successfully.`,
          type: 'SUCCESS',
        }),
      );
    } else {
      yield put(
        showToast({
          message: `Response code: 200. Webhook deleted successfully.`,
          type: 'SUCCESS',
        }),
      );
    }
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getMerchantConfig({ data: { cb } }: any) {
  try {
    const { url, method } = GET_MERCHANT_CONFIG_URL;

    const response = yield call(request, url, { method });
    cb(response);
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* updateMerchantConfig({ data: { cb, actions, ...body } }: any) {
  try {
    const { url, method } = UPDATE_MERCHANT_CONFIG_URL;
    const options = { method, body: JSON.stringify(body) };
    const response = yield call(request, url, options);
    yield put(
      showToast({
        message: `Response code: 200. Webhook ${
          body.webhook_url ? 'added' : 'deleted'
        } successfully.`,
        type: 'SUCCESS',
      }),
    );
    cb(response);
  } catch (err) {
    yield put(showToast(err));
  } finally {
    if (actions) actions.setSubmitting(false);
    else yield put(disableLoaderAction());
  }
}

export function* addNotificationChannelDetails({
  data: { cb, key, actions, ...body },
}: any) {
  try {
    const { url, method } = ADD_NOTIFICATION_CHANNEL_DETAILS_URL;
    const options = {
      method,
      body: JSON.stringify(body),
      headers: {
        [AUDIT_HEADER]: DEVELOPER_WEBHOOK_AUDIT,
      },
    };
    const response = yield call(request, url, options);
    // adding cfApiVersion because backend is not sending
    if (body.cfApiVersion) response.cfApiVersion = body.cfApiVersion;
    cb({ ...(response || body) });
    yield put(
      showToast({
        message: `Response code: 200. Webhook added successfully.`,
        type: 'SUCCESS',
      }),
    );
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  } finally {
    actions.setSubmitting(false);
  }
}

export function* testWebhook({
  data: { data, actions, cb, uri, errorCb },
}: any) {
  try {
    const { url, method } = TEST_WEBHOOK_URL;
    let body = data;
    if (uri) {
      body = {
        ...data,
        uri,
      };
    }
    const options = {
      method,
      body: JSON.stringify(body),
    };
    const response = yield call(request, url, options);
    if (cb) cb(response);
  } catch (err) {
    if (errorCb) errorCb();
    yield put(showToast(err));
  } finally {
    if (actions) actions.setSubmitting(false);
  }
}

export function* getMerchantKey({ data: { cb } }: any) {
  try {
    const { url, method } = FETCH_API_KEYS_URL;
    const response = yield call(request, url, { method });
    if (cb) cb(response);
    // eslint-disable-next-line no-empty
  } catch (err) {}
}

export function* getSubscriptionsWebhook({ data: { cb } }: any) {
  try {
    const { url, method } = GET_SUBSCRIPTIONS_WEBHOOK_URL;
    const response = yield call(request, url, { method });
    cb(response);
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* updateSubscriptionsWebhook({ data: { cb, ...body } }: any) {
  try {
    const { url, method } = UPDATE_SUBSCRIPTIONS_WEBHOOK_URL;
    const options = { method, body: JSON.stringify(body) };
    const response = yield call(request, url, options);
    cb(response);
    yield put(
      showToast({
        message: `Response code: 200. Webhook ${
          body.webhookUrl ? 'added' : 'deleted'
        } successfully.`,
        type: 'SUCCESS',
      }),
    );
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* createBulkNotificationChannel({
  data: { data, cb, mode },
}: any) {
  try {
    const { url, method } = CREATE_BULK_NOTIFICATION_CHANNEL_URL;
    const options = {
      method,
      body: JSON.stringify({ channelDetails: data }),
      headers: { [AUDIT_HEADER]: DEVELOPER_WEBHOOK_AUDIT },
    };
    const response = yield call(request, url, options);

    if (mode === EDIT) {
      yield put(
        showToast({
          message: 'Response code: 200. Webhook updated successfully',
          type: 'SUCCESS',
        }),
      );
    } else {
      yield put(
        showToast({
          message: 'Response code: 200. Webhook added successfully',
          type: 'SUCCESS',
        }),
      );
    }

    if (cb) cb(response);
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getConfiguredWebhooks({ data: { cb } }: any) {
  try {
    const { url, method } = GET_CONFIGURED_WEBHOOKS_URL;
    const response = yield call(request, url, method);
    cb(response);
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getAllNotificationTypes({
  data: { cfApiVersion, cb, errorCb },
}: any) {
  try {
    const { url, method } = GET_ALL_NOTIFICATION_TYPES_URL;
    const body = { cfApiVersion, notificationChannel: 'WEBHOOK' };
    const options = {
      method,
      queryString: body,
    };
    const response = yield call(request, url, options);
    if (cb) cb(response);
  } catch (err) {
    yield put(showToast(err));
    errorCb(err);
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* updateWebhookVersions({ data: { data, cb } }: any) {
  try {
    const { url, method } = UPDATE_WEBHOOK_NOTIFICATIONS_STATUS_URL;
    const promiseArr = [];
    data.forEach(item => {
      promiseArr.push(
        // eslint-disable-next-line redux-saga/yield-effects
        call(request, url, { method, body: JSON.stringify(item) }),
      );
    });
    const response = yield all(promiseArr);
    if (cb) cb(response);
  } catch (error) {
    yield put(showToast(error));
  }
}

export function* getAbandonedCheckoutSecretKey({data : {}}) {
  try {
    const { url, method } = GET_ABANDONED_CHECKOUT_SECRET_KEY_URL;
    const options = {
      method,
      queryString: { configKey: 'ABANDONED_CHECKOUT_SECRET' },
    };
    try {
      const response = yield call(request, url, options);
      const abandonedCheckoutSecretKey = response.find(item => item?.configKey === 'ABANDONED_CHECKOUT_SECRET' && item?.status === 'ACTIVE')?.configValue
      if(abandonedCheckoutSecretKey){
        yield put(updateAbandonedCheckoutSecretKeyAction(abandonedCheckoutSecretKey))
      }
    } catch (requestError) {
      console.error('Request Error:', requestError);
    }
  } catch (err) {
    yield put(showToast(err));
  } 
}

export function* saveAbandondedCheckoutWebhook({ data: { data, cb, mode } }: any) {
  try {
    const { url, method } = mode === 'EDIT'? UPDATE_ABANDONED_CHECKOUT_SECRET_KEY_URL: SAVE_ABANDONED_CHECKOUT_WEBHOOK_URL;
    const options = {
      method,
      body: JSON.stringify(data),
    };
    const response = yield call(request, url, options);
    if(response){
      yield put(updateAbandonedCheckoutSecretKeyAction(data?.configValue))
      if(cb && typeof cb === 'function'){
        cb()
      }
      yield put(
        showToast({
          message: `Webhook: Abandoned Secret key updated successfully.`,
          type: 'SUCCESS',
        }),
      );
    }
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* getAllNotificationTypesSaga() {
  yield takeEvery(GET_ALL_NOTIFICATION_TYPES, getAllNotificationTypes);
}

export function* getConfiguredWebhooksSaga() {
  yield takeEvery(GET_CONFIGURED_WEBHOOKS, getConfiguredWebhooks);
}

export function* getWebhookNotificationsSaga() {
  yield takeEvery(GET_WEBHOOK_NOTIFICATIONS, getWebhookNotifications);
}

export function* createBulkNotificationChannelSaga() {
  yield takeEvery(
    CREATE_BULK_NOTIFICATION_CHANNEL,
    createBulkNotificationChannel,
  );
}

export function* getNotificationChannelDetailsSaga() {
  yield takeEvery(
    GET_NOTIFICATION_CHANNEL_DETAILS,
    getNotificationChannelDetails,
  );
}

export function* updateNotificationChannelDetailsSaga() {
  yield takeEvery(
    UPDATE_NOTIFICATION_CHANNEL_DETAILS,
    updateNotificationChannelDetails,
  );
}

export function* getMerchantConfigSaga() {
  yield takeEvery(GET_MERCHANT_CONFIG, getMerchantConfig);
}

export function* updateMerchantConfigSaga() {
  yield takeEvery(UPDATE_MERCHANT_CONFIG, updateMerchantConfig);
}

export function* addNotificationChannelDetailsSaga() {
  yield takeEvery(
    ADD_NOTIFICATION_CHANNEL_DETAILS,
    addNotificationChannelDetails,
  );
}

export function* getAllNotificationVersionsSaga() {
  yield takeLatest(GET_ALL_NOTIFICATION_VERSIONS, getAllNotificationVersions);
}

export function* getAbandonedCheckoutSecretKeySaga() {
  yield takeLatest(
    GET_ABANDONED_CHECKOUT_SECRET_KEY as any,
    getAbandonedCheckoutSecretKey,
  );
}

export function* saveAbandondedCheckoutWebhookSaga() {
  yield takeLatest(
    SAVE_ABANDONED_CHECKOUT_WEBHOOK,
    saveAbandondedCheckoutWebhook,
  );
}

export function* testWebhookSaga() {
  yield takeLatest(TEST_WEBHOOK, testWebhook);
}

export function* getMerchantKeySaga() {
  yield takeLatest(GET_MERCHANT_KEY, getMerchantKey);
}

export function* getSubscriptionsWebhookSaga() {
  yield takeLatest(GET_SUBSCRIPTIONS_WEBHOOK, getSubscriptionsWebhook);
}

export function* updateSubscriptionsWebhookSaga() {
  yield takeLatest(UPDATE_SUBSCRIPTIONS_WEBHOOK, updateSubscriptionsWebhook);
}

export function* updateWebhookVersionsSaga() {
  yield takeLatest(UPDATE_WEBHOOK_VERSIONS, updateWebhookVersions);
}

// Individual exports for testing
export default function* webhookNotificationsSaga() {
  // See example in containers/HomePage/saga.js
  const sagas = [
    getWebhookNotificationsSaga,
    // updateWebhookNotificationsStatusSaga,
    getNotificationChannelDetailsSaga,
    getMerchantConfigSaga,
    updateMerchantConfigSaga,
    updateNotificationChannelDetailsSaga,
    addNotificationChannelDetailsSaga,
    testWebhookSaga,
    getMerchantKeySaga,
    getSubscriptionsWebhookSaga,
    updateSubscriptionsWebhookSaga,
    getConfiguredWebhooksSaga,
    getAllNotificationTypesSaga,
    createBulkNotificationChannelSaga,
    getAllNotificationVersionsSaga,
    updateWebhookVersionsSaga,
    getAbandonedCheckoutSecretKeySaga,
    saveAbandondedCheckoutWebhookSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
